import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Tv, Film, Mic, ArrowLeft, MapPin, UserPlus, UserCheck, UserX, Users, Clock, Check, X, Heart, List, Trophy, BarChart3, Loader2, Lock, BookOpen, MessageSquare, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';
import api from '../lib/api';

export default function PublicProfilePage({ user }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const profileRes = await api.get(`/profile/public/${userId}`);
      setProfile(profileRes.data);
      
      // If can view full profile, fetch stats and status
      if (profileRes.data.can_view_full_profile) {
        try {
          const statsRes = await api.get(`/profile/stats/${userId}`);
          setStats(statsRes.data);
        } catch {
          // eslint-disable-next-line no-empty -- Intentional: Stats may require follow relationship, silent fail shows "Follow to see stats" UI
        }
        
        // Fetch user's status
        try {
          const statusRes = await api.get(`/social/status/${userId}`);
          setUserStatus(statusRes.data);
        } catch {
          // Status fetch failed - user may not have a status set
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('User not found');
        navigate(-1);
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFollowRequest = async () => {
    setActionLoading(true);
    try {
      await api.post(`/follow/request/${userId}`, {});
      setProfile(prev => ({ ...prev, pending_request: true }));
      toast.success('Follow request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfollow = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/follow/${userId}`);
      setProfile(prev => ({ ...prev, is_following: false, can_view_full_profile: prev.is_followed_by }));
      setStats(null);
      toast.success('Unfollowed');
    } catch (error) {
      toast.error('Failed to unfollow');
    } finally {
      setActionLoading(false);
    }
  };

  const getDisplayName = () => profile?.display_name || profile?.name || 'User';
  const getInitial = () => getDisplayName().charAt(0).toUpperCase();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const interests = profile?.interests || ['tv', 'movies', 'podcasts'];
  const canViewFull = profile.can_view_full_profile;

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} />

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Profile Header Card */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6 sm:mb-8">
          <CardContent className="p-4 sm:p-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4 sm:gap-6">
              {/* Avatar */}
              <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-[#F87171]">
                {profile?.avatar_url ? (
                  <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />
                ) : profile?.picture ? (
                  <AvatarImage src={profile.picture} alt={getDisplayName()} />
                ) : (
                  <AvatarFallback className="bg-[#F87171] text-white text-3xl sm:text-4xl font-bold">
                    {getInitial()}
                  </AvatarFallback>
                )}
              </Avatar>
              
              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3D4F6F] mb-2">{getDisplayName()}</h1>
                
                {profile?.country && (
                  <div className="flex items-center justify-center md:justify-start gap-2 text-[#64748B] mb-4">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.country}</span>
                  </div>
                )}
                
                {/* Follower Stats */}
                <div className="flex items-center justify-center md:justify-start gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#3D4F6F]">{profile.followers_count || 0}</p>
                    <p className="text-sm text-[#64748B]">Followers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#3D4F6F]">{profile.following_count || 0}</p>
                    <p className="text-sm text-[#64748B]">Following</p>
                  </div>
                </div>
                
                {/* Interests */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-4">
                  {interests.includes('tv') && (
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Tv className="w-3 h-3" /> TV Shows
                    </span>
                  )}
                  {interests.includes('movies') && (
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Film className="w-3 h-3" /> Movies
                    </span>
                  )}
                  {interests.includes('podcasts') && (
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Mic className="w-3 h-3" /> Podcasts
                    </span>
                  )}
                </div>
                
                {/* User's Current Statuses */}
                {canViewFull && userStatus && (userStatus.tv_status || userStatus.movie_status || userStatus.podcast_status) && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-[#F87171]/10 to-[#F87171]/5 rounded-xl">
                    <p className="text-xs font-medium text-[#64748B] mb-2 uppercase tracking-wide">Currently Watching/Listening</p>
                    <div className="flex flex-wrap gap-2">
                      {userStatus.tv_status && (
                        <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-sm">
                          <Tv className="w-4 h-4 text-purple-500" />
                          <span className="text-sm text-[#3D4F6F]">{userStatus.tv_status}</span>
                        </div>
                      )}
                      {userStatus.movie_status && (
                        <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-sm">
                          <Film className="w-4 h-4 text-red-500" />
                          <span className="text-sm text-[#3D4F6F]">{userStatus.movie_status}</span>
                        </div>
                      )}
                      {userStatus.podcast_status && (
                        <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-sm">
                          <Mic className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-[#3D4F6F]">{userStatus.podcast_status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Follow Button */}
                {!profile.is_own_profile && (
                  <div className="flex items-center justify-center md:justify-start gap-2">
                    {profile.is_following ? (
                      <Button
                        variant="outline"
                        onClick={handleUnfollow}
                        disabled={actionLoading}
                        className="rounded-full"
                        data-testid="unfollow-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
                        Following
                      </Button>
                    ) : profile.pending_request ? (
                      <Button
                        variant="outline"
                        disabled
                        className="rounded-full"
                        data-testid="pending-btn"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Request Pending
                      </Button>
                    ) : (
                      <Button
                        onClick={handleFollowRequest}
                        disabled={actionLoading}
                        className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                        data-testid="follow-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                        Request to Follow
                      </Button>
                    )}
                    
                    {profile.is_followed_by && (
                      <span className="text-sm text-[#64748B] flex items-center gap-1">
                        <Users className="w-4 h-4" /> Follows you
                      </span>
                    )}
                  </div>
                )}
                
                {/* Mutual Friends Actions - Show message and view content buttons */}
                {!profile.is_own_profile && profile.is_following && profile.is_followed_by && (
                  <div className="flex items-center justify-center md:justify-start gap-2 mt-3">
                    <Button
                      onClick={() => navigate(`/messages/${profile.user_id}`)}
                      variant="outline"
                      className="rounded-full border-[#F87171] text-[#F87171] hover:bg-[#F87171]/10"
                      data-testid="message-friend-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                    <Button
                      onClick={() => navigate(`/friend/${profile.user_id}/content`)}
                      className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                      data-testid="view-friend-content-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Lists & History
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Follow to see more - shown when not following */}
        {!canViewFull && !profile.is_own_profile && (
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6 sm:mb-8">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-outfit text-xl font-bold text-[#3D4F6F] mb-2">Private Profile</h3>
              <p className="text-[#64748B] mb-4">
                {profile.pending_request 
                  ? `Your follow request is pending. Once ${getDisplayName()} accepts, you'll be able to see their full profile and stats.`
                  : `Follow ${getDisplayName()} to see their watchlists, rankings, and stats.`
                }
              </p>
              {!profile.pending_request && !profile.is_following && (
                <Button
                  onClick={handleFollowRequest}
                  disabled={actionLoading}
                  className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                  data-testid="follow-to-view-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Request to Follow
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Statistics (only visible if can view full profile) */}
        {canViewFull && stats && (
          <>
            <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6" />
              Statistics
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {stats?.stats?.tv && (
                <StatCard 
                  title="TV Shows" 
                  icon={<Tv className="w-4 h-4 text-purple-600" />}
                  iconBg="bg-purple-100"
                  stats={stats.stats.tv}
                  type="tv"
                />
              )}
              {stats?.stats?.movies && (
                <StatCard 
                  title="Movies" 
                  icon={<Film className="w-4 h-4 text-red-600" />}
                  iconBg="bg-red-100"
                  stats={stats.stats.movies}
                  type="movies"
                />
              )}
              {stats?.stats?.podcasts && (
                <StatCard 
                  title="Podcasts" 
                  icon={<Mic className="w-4 h-4 text-blue-600" />}
                  iconBg="bg-blue-100"
                  stats={stats.stats.podcasts}
                  type="podcasts"
                />
              )}
            </div>
          </>
        )}

        {/* Own Profile Link */}
        {profile.is_own_profile && (
          <div className="text-center">
            <Button
              onClick={() => navigate('/profile')}
              className="bg-white text-[#F87171] hover:bg-white/90 rounded-full"
            >
              Go to Your Full Profile
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ title, icon, iconBg, stats, type }) {
  const watchedLabel = type === 'podcasts' ? 'Listened' : 'Watched';
  const totalKey = type === 'podcasts' ? 'total_listened' : 'total_watched';
  
  return (
    <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
          <div className={`w-8 h-8 ${iconBg} rounded-lg flex items-center justify-center`}>
            {icon}
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[#3D4F6F]">{stats[totalKey] || 0}</div>
            <div className="text-xs text-[#64748B] flex items-center justify-center gap-1">
              <Check className="w-4 h-4" />
              {watchedLabel}
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[#3D4F6F]">{stats.total_in_lists || 0}</div>
            <div className="text-xs text-[#64748B] flex items-center justify-center gap-1">
              <List className="w-4 h-4" />
              In Lists
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.liked || 0}</div>
            <div className="text-xs text-[#64748B] flex items-center justify-center gap-1">
              <Heart className="w-4 h-4 text-green-500" />
              Liked
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.disliked || 0}</div>
            <div className="text-xs text-[#64748B] flex items-center justify-center gap-1">
              <X className="w-4 h-4 text-red-500" />
              Disliked
            </div>
          </div>
        </div>
        
        {stats.favorite && (
          <div className="pt-3 border-t">
            <p className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
              <Trophy className="w-3 h-3 text-amber-500" /> #1 Favorite
            </p>
            <p className="font-medium text-[#3D4F6F] truncate">{stats.favorite.title}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
